import {useEffect, useState} from "react";
import {Button, Modal} from "antd";
import {ClockCircleOutlined, DesktopOutlined, ThunderboltOutlined} from "@ant-design/icons";
import {useRecoilValue} from "recoil";
import {Trans, useTranslation} from "react-i18next";
import {globalBrandSettingState, globalUserVerifyState} from "../global_state";
import {holdDialog} from "../dialogGate";
import {PASSKEY_PROMPT_Z_INDEX} from "../passkeyPromptZIndex";
import {appIconFor, appNameFor} from "./manifest";
import useInstallPrompt from "./useInstallPrompt";
import useInstallOfferGate from "./useInstallOfferGate";
import "./installAppModal.css";

// each benefit carries its own icon, so the list reads at a glance rather than as a row of ticks
const BENEFITS = [
    ["pwaInstall.ownWindow", "Opens in its own window, apart from your browser tabs", DesktopOutlined],
    ["pwaInstall.launch", "Start it from your taskbar, Dock or desktop like any other app", ThunderboltOutlined],
];

// Safari 17 on macOS: no install prompt to call, but the menu path is short and stable.
// The first step is one sentence with the menu items placed inside it, so a language that puts
// the verb last can still order it naturally.
const SAFARI_MENU_STEP = [
    "pwaInstall.safariStepMenu",
    "In the menu bar, choose <file>File</file> › <add>Add to Dock…</add>",
];
const SAFARI_STEPS = [
    ["pwaInstall.safariStepAdd", "Check the name, then click Add."],
    ["pwaInstall.safariStepOpen", "Open {{name}} from your Dock or Launchpad."],
];

// Each layout scopes its own buttons, panel and theme under #layout-N, so the offer renders inside
// the layout on screen and wears that layout's real primary button instead of a copy that would
// drift. Without a layout (logged out, tests) it portals to body.
const LAYOUT_IDS = ["layout-1", "layout-2", "layout-3"];

const layoutContainer = () =>
    LAYOUT_IDS.map((id) => document.getElementById(id)).find(Boolean) || document.body;

// Chrome and Edge's own install button in the address bar: a screen with a down arrow
const AddressBarInstallIcon = () => (
    <svg className={"pwa-install__omnibox-icon"} viewBox={"0 0 16 16"} aria-hidden={"true"} focusable={"false"}>
        <path
            d={"M2.5 3h11a1 1 0 0 1 1 1v6.5a1 1 0 0 1-1 1h-11a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1Z M6 14h4 M8 11.5V14"}
            fill={"none"}
            stroke={"currentColor"}
            strokeWidth={"1.3"}
            strokeLinecap={"round"}
        />
        <path
            d={"M8 5v3.6 M6.3 7.1 8 8.8l1.7-1.7"}
            fill={"none"}
            stroke={"currentColor"}
            strokeWidth={"1.3"}
            strokeLinecap={"round"}
            strokeLinejoin={"round"}
        />
    </svg>
);

// offered once per page, on desktop, to a logged-in client whose browser can install this brand
const InstallAppModal = () => {
    const {t} = useTranslation();
    const brandSetting = useRecoilValue(globalBrandSettingState);
    const verifyState = useRecoilValue(globalUserVerifyState);
    const {brand, platform, available, canOffer, promptInstall, dismiss} = useInstallPrompt();
    const gateOpen = useInstallOfferGate(canOffer);

    const [open, setOpen] = useState(false);
    // the variant is fixed when the dialog opens: the prompt is spent by the click, and the
    // dialog must not change shape under the client while it closes
    const [variant, setVariant] = useState(null);
    // layout 2 sets the actions side by side, dismiss first, as its own dialogs do; the DOM follows
    // the visual order so Tab does not run right to left
    const [sideBySide, setSideBySide] = useState(false);
    const [offered, setOffered] = useState(false);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (gateOpen && !offered) {
            setVariant(platform);
            setSideBySide(!!document.getElementById("layout-2"));
            setOffered(true);
            setOpen(true);
        }
    }, [gateOpen, offered, platform]);

    // our own hold closes the gate, which is why the dialog latches instead of following it
    useEffect(() => {
        if (open) return holdDialog("installOffer");
    }, [open]);

    // logged out, or installed some other way (the browser's own address bar button): the ask is
    // over, and neither is the client saying no, so nothing is recorded
    useEffect(() => {
        if (open && !busy && (!verifyState || !available)) setOpen(false);
    }, [open, busy, verifyState, available]);

    // the name the app will carry once installed, so the offer and the Dock agree
    const name = appNameFor(brand) || brandSetting?.companyName || "";
    const icon = appIconFor(brand);

    const notNow = () => {
        // the browser's own dialog is up: its answer is the one that counts, once
        if (busy) return;
        dismiss();
        setOpen(false);
    };

    const install = async () => {
        setBusy(true);
        // accepted and dismissed are recorded by the core; unavailable just closes
        await promptInstall();
        setBusy(false);
        setOpen(false);
    };

    const safari = variant === "safari-mac";

    const notNowButton = !safari && (
        <Button type={"text"} block className={"pwa-install__secondary"} onClick={notNow} disabled={busy}>
            {t("pwaInstall.notNow", {defaultValue: "Not now"})}
        </Button>
    );

    return (
        <Modal
            open={open}
            title={
                safari
                    ? t("pwaInstall.safariTitle", {defaultValue: "Add {{name}} to your Dock", name})
                    : t("pwaInstall.title", {defaultValue: "Install the {{name}} app", name})
            }
            onCancel={notNow}
            // nothing closes this one while the browser's install dialog is answering for it
            closable={!busy}
            footer={null}
            destroyOnClose
            width={400}
            centered
            getContainer={layoutContainer}
            // inline, so it also beats a layout's own modal band (#layout-3 .ant-modal-wrap: 1200)
            // and clears the layout chrome wherever the dialog lands
            zIndex={PASSKEY_PROMPT_Z_INDEX}
            maskClosable={!busy}
            keyboard={!busy}
        >
            <div className={"pwa-install"}>
                <div className={"pwa-install__dock"} aria-hidden={"true"}>
                    {icon && <img className={"pwa-install__tile"} src={icon} alt={""} width={64} height={64} />}
                    <span className={"pwa-install__app-name"}>{name}</span>
                    <span className={"pwa-install__shelf"} />
                </div>

                {/* everything but the tile, so a wide layout can set it beside the tile */}
                <div className={"pwa-install__body"}>
                    {safari ? (
                        <>
                            {/* role restates the list: Safari drops it once list-style is none, and this
                                variant only ever renders in Safari */}
                            {/* eslint-disable-next-line jsx-a11y/no-redundant-roles */}
                            <ol className={"pwa-install__list"} role={"list"}>
                                <li className={"pwa-install__item"}>
                                    <span className={"pwa-install__marker"}>1</span>
                                    <span>
                                        <Trans
                                            t={t}
                                            i18nKey={SAFARI_MENU_STEP[0]}
                                            defaults={SAFARI_MENU_STEP[1]}
                                            components={{
                                                file: <span className={"pwa-install__menu-item"} />,
                                                add: <span className={"pwa-install__menu-item"} />,
                                            }}
                                        />
                                    </span>
                                </li>
                                {SAFARI_STEPS.map(([key, text], index) => (
                                    <li key={key} className={"pwa-install__item"}>
                                        <span className={"pwa-install__marker"}>{index + 2}</span>
                                        <span>{t(key, {defaultValue: text, name})}</span>
                                    </li>
                                ))}
                            </ol>
                            <p className={"pwa-install__note"}>
                                {t("pwaInstall.safariShareNote", {
                                    defaultValue: "You can also use the Share button in the toolbar and choose Add to Dock.",
                                })}
                            </p>
                        </>
                    ) : (
                        // eslint-disable-next-line jsx-a11y/no-redundant-roles -- WebKit drops list semantics under list-style: none
                        <ul className={"pwa-install__list"} role={"list"}>
                            {BENEFITS.map(([key, text, Icon]) => (
                                <li key={key} className={"pwa-install__item pwa-install__item--icon"}>
                                    <span className={"pwa-install__marker pwa-install__marker--icon"} aria-hidden={"true"}>
                                        <Icon />
                                    </span>
                                    <span>{t(key, {defaultValue: text})}</span>
                                </li>
                            ))}
                            {/* "not now" is not "never": the browser keeps its own install button around */}
                            <li className={"pwa-install__item pwa-install__item--icon"}>
                                <span className={"pwa-install__marker pwa-install__marker--icon"} aria-hidden={"true"}>
                                    <ClockCircleOutlined />
                                </span>
                                <span>
                                    <Trans
                                        t={t}
                                        i18nKey={"pwaInstall.addressBarNote"}
                                        defaults={"Not now? Install it any time from the <icon/> button in your browser's address bar."}
                                        components={{icon: <AddressBarInstallIcon />}}
                                    />
                                </span>
                            </li>
                        </ul>
                    )}

                    <div className={`pwa-install__actions${sideBySide && !safari ? " pwa-install__actions--row" : ""}`}>
                        {sideBySide && notNowButton}
                        <Button
                            type={"primary"}
                            block
                            className={"pwa-install__primary"}
                            loading={busy}
                            aria-busy={busy}
                            onClick={safari ? notNow : install}
                        >
                            {safari
                                ? t("pwaInstall.gotIt", {defaultValue: "Got it"})
                                : t("pwaInstall.install", {defaultValue: "Install"})}
                        </Button>
                        {!sideBySide && notNowButton}
                    </div>
                </div>
            </div>
        </Modal>
    );
};

export default InstallAppModal;
